"use client";

import { useState } from "react";
import { X } from "lucide-react";
import { motion } from "framer-motion";

import { Badge } from "@/shadcnComponents/ui/badge";
import { Button } from "@/shadcnComponents/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/shadcnComponents/ui/card";

interface SyncAccountPopupProps {
  onSync: () => void;
  onDismiss: () => void;
}

export function SyncAccountPopup({ onSync, onDismiss }: SyncAccountPopupProps) {
  const [isVisible, setIsVisible] = useState(true);

  const handleDismiss = () => {
    setIsVisible(false);
    onDismiss();
  };

  const handleSync = () => {
    setIsVisible(false);
    onSync();
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm">
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="relative w-full flex items-center justify-center max-w-[550px]"
      >
        <Card className="w-full max-w-md animate-in fade-in zoom-in duration-300 border-0 bg-gradient-to-br from-zinc-900 via-zinc-900 to-zinc-800 shadow-[0_0_30px_rgba(138,43,226,0.3)]">
          <CardHeader className="relative pb-2">
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-2 top-2 text-zinc-400 hover:bg-zinc-800 hover:text-white"
              onClick={handleDismiss}
            >
              <X className="h-4 w-4" />
            </Button>
            <div className="mb-2 bg-gradient-to-r from-violet-500 via-fuchsia-400 to-pink-500 bg-clip-text text-transparent">
              <CardTitle className="text-2xl font-bold">
                Sync Your Account
              </CardTitle>
            </div>
            <CardDescription className="text-zinc-400">
              Keep your profile updated and access the latest opportunities!
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 pt-2">
            <p className="text-sm text-zinc-400">
              Stay ahead with exclusive perks:
            </p>
            <div className="flex flex-wrap gap-2">
              {[
                "Increased Visibility",
                "Verified Status",
                "Priority Collaborations",
                "Content Monetization",
                "Performance Analytics",
              ].map((perk) => (
                <Badge
                  key={perk}
                  className="bg-violet-500/20 text-violet-200 hover:bg-violet-500/30 px-3 py-1"
                >
                  {perk}
                </Badge>
              ))}
            </div>
            <div className="rounded-lg bg-zinc-800/50 p-4 shadow-inner">
              <h4 className="mb-3 font-medium text-white">
                Why creators love syncing:
              </h4>
              <ul className="space-y-3 text-sm">
                <li className="flex items-start">
                  <span className="mr-2 text-violet-400">✦</span>
                  <span className="text-zinc-300">
                    Get prioritized by brands for paid collaborations
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2 text-violet-400">✦</span>
                  <span className="text-zinc-300">
                    Understand your audience with real-time analytics
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2 text-violet-400">✦</span>
                  <span className="text-zinc-300">
                    Access exclusive features, early
                  </span>
                </li>
              </ul>
            </div>
          </CardContent>
          <CardFooter className="flex-col gap-1">
            <span className="text-zinc-300 text-sm">
              Don’t miss out. Sync now and stay on top!
            </span>
            <Button
              className="w-full bg-gradient-to-r from-violet-600 via-fuchsia-600 to-pink-600 text-white shadow-lg shadow-purple-900/20 hover:from-violet-500 hover:via-fuchsia-500 hover:to-pink-500"
              size="lg"
              onClick={handleSync}
            >
              Sync My Account Now
            </Button>
          </CardFooter>
        </Card>
      </motion.div>
    </div>
  );
}
